import {
  ArrowLeft,
  Ban,
  ChevronRight,
  Flag,
  MoreHorizontal,
  Server,
  UserMinus,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button, Popover, PopoverContent, PopoverTrigger, cn } from "@link/ui";

import { api, type Space } from "../lib/api";
import { ignoreAccount } from "../lib/ignored";
import { useSelf } from "../state/SelfSession";

type ReportReason = "spam" | "harassment" | "csam" | "illegal" | "other";

export function UserActionsMenu({
  accountXid,
  displayName,
  buttonClassName,
  excludeSpaceXid,
}: {
  accountXid: string;
  displayName: string;
  buttonClassName?: string;
  excludeSpaceXid?: string;
}) {
  const self = useSelf();
  const [open, setOpen] = useState(false);
  const [showServers, setShowServers] = useState(false);
  const [servers, setServers] = useState<Space[]>([]);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason>("harassment");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isSelf = accountXid === self.identity.accountXid;

  const loadServers = async (): Promise<void> => {
    setBusy(true);
    setStatus(null);
    try {
      const result = await api.spaces.list();
      const candidates = result.spaces.filter((space) => space.space_type !== "personal_workspace" && space.xid !== excludeSpaceXid);
      const manageable = await Promise.all(
        candidates.map(async (space) => {
          try {
            const members = await api.spaces.members(space.xid);
            const me = members.members.find((member) => member.account_id === self.identity.accountXid);
            return me?.is_owner === true || me?.is_admin === true ? space : null;
          } catch {
            return null;
          }
        }),
      );
      setServers(manageable.filter((space): space is Space => space !== null));
      setShowServers(true);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Couldn't load your servers.");
    } finally {
      setBusy(false);
    }
  };

  const invite = async (space: Space): Promise<void> => {
    setBusy(true);
    try {
      const created = await api.spaces.createInvite(space.xid);
      const url = `${window.location.origin}/invite/${created.xid}`;
      await navigator.clipboard.writeText(url);
      setStatus(`Invite to ${space.name} copied.`);
      setShowServers(false);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Couldn't create an invite.");
    } finally {
      setBusy(false);
    }
  };

  const block = async (): Promise<void> => {
    if (!window.confirm(`Block ${displayName}? They won't be able to message or add you.`)) return;
    setBusy(true);
    try {
      await api.friends.block({ account_xid: accountXid });
      setStatus(`${displayName} was blocked.`);
      setOpen(false);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Couldn't block this account.");
    } finally {
      setBusy(false);
    }
  };

  const report = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.friends.report(accountXid, {
        reason,
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
      });
      setReportOpen(false);
      setOpen(false);
      setNote("");
      setStatus("Report sent for review.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Couldn't send this report.");
    } finally {
      setBusy(false);
    }
  };

  if (isSelf) return null;

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setShowServers(false); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="More"
            aria-label={`More actions for ${displayName}`}
            onClick={(event) => event.stopPropagation()}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-cloud-700 ring-1 ring-white/50 backdrop-blur transition-colors hover:bg-white",
              buttonClassName,
            )}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={8}
          sticky="always"
          className="max-h-[calc(100dvh-1rem)] w-60 overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-[#232428] p-1.5 text-sm text-white shadow-2xl"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {showServers ? (
            <>
              <button type="button" onClick={() => setShowServers(false)} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-white/10">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <div className="my-1 h-px bg-white/10" />
              {servers.length === 0 ? (
                <p className="px-2 py-3 text-xs text-white/60">You don't manage a server yet.</p>
              ) : (
                servers.map((space) => (
                  <button key={space.xid} type="button" disabled={busy} onClick={() => void invite(space)} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-white/10 disabled:opacity-50">
                    <Server className="h-4 w-4" />
                    <span className="truncate">{space.name}</span>
                  </button>
                ))
              )}
            </>
          ) : (
            <>
              <button type="button" disabled={busy} onClick={() => void loadServers()} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-white/10 disabled:opacity-50">
                <Server className="h-4 w-4" />
                <span className="flex-1">Invite to Server</span>
                <ChevronRight className="h-4 w-4" />
              </button>
              <div className="my-1 h-px bg-white/10" />
              <button
                type="button"
                onClick={() => {
                  ignoreAccount(accountXid);
                  setStatus(`${displayName} is now ignored on this device.`);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-white/10"
              >
                <UserMinus className="h-4 w-4" /> Ignore
              </button>
              <button type="button" disabled={busy} onClick={() => void block()} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-red-400 hover:bg-red-950/30 disabled:opacity-50">
                <Ban className="h-4 w-4" /> Block
              </button>
              <button type="button" onClick={() => { setOpen(false); setReportOpen(true); }} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-red-400 hover:bg-red-950/30">
                <Flag className="h-4 w-4" /> Report this user
              </button>
            </>
          )}
          {status !== null ? <p className="mt-1 rounded-lg bg-white/10 px-2 py-2 text-xs text-white/75">{status}</p> : null}
        </PopoverContent>
      </Popover>

      {reportOpen ? (
        <div className="fixed inset-0 z-[240] flex items-center justify-center bg-black/65 p-4" onMouseDown={(event) => event.stopPropagation()}>
          <div className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-[#232428] p-5 text-white shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Report {displayName}</h2>
              <button type="button" onClick={() => setReportOpen(false)} className="rounded-lg p-2 text-white/60 hover:bg-white/10"><X className="h-4 w-4" /></button>
            </div>
            <select value={reason} onChange={(event) => setReason(event.target.value as ReportReason)} className="mt-4 h-10 w-full rounded-lg border border-white/15 bg-[#17181b] px-3 text-sm text-white outline-none">
              <option value="harassment">Harassment</option>
              <option value="spam">Spam</option>
              <option value="illegal">Illegal content</option>
              <option value="csam">Child safety concern</option>
              <option value="other">Other</option>
            </select>
            <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={2048} rows={4} placeholder="Tell us what happened" className="mt-3 w-full resize-none rounded-lg border border-white/15 bg-[#17181b] px-3 py-2 text-sm text-white outline-none placeholder:text-white/35" />
            <Button className="mt-4 w-full" disabled={busy} onClick={() => void report()}>
              {busy ? "Sending…" : "Send report"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
